import {useCallback, useEffect} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch} from 'react-redux';
import urls from 'urls';
import {apiClient} from '~/api/api-client';
import {useRequestLogin} from '~/common/fabric/Card/components/LoginModal/hooks/useRequestLogin';
import {subscribeToUserStateChanges, type UserData} from '~/common/utils/userStateSync';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useTracking} from '~library/context/tracking/tracking-context';
import {LoginModalWrapper} from '~library/organisms/LoginModal/LoginModalWrapper';
import {userLoggedIn} from '~library/organisms/LoginModal/reducer/actions';
import {AccountPreview} from './components/AccountPreview/AccountPreview';
import * as styles from './Login.module.scss';

export const Login = () => {
  const dispatch = useDispatch();
  const {loggedIn, requestLogin} = useRequestLogin();
  const {track} = useTracking();

  const fetchFavorites = useCallback(async () => {
    const response = await apiClient.get(urls.get('ajax_gallery_favorites'));
    const data = await response.json();
    // this is to match the current gallery implementation
    window.favoritesNumber = data?.pm_templates_count + data?.templates_count;
  }, []);

  const onRequestLoginHandler = useCallback(async () => {
    track({
      dataQaId: 'login',
    });
    if (await requestLogin()) {
      window.scrollTo(0, 0);
      await fetchFavorites();
    }
  }, [requestLogin, fetchFavorites, track]);

  // Subscribe to user state changes from other stores
  useEffect(() => {
    return subscribeToUserStateChanges((userData: UserData) => {
      if (userData?.userId) {
        dispatch(
          userLoggedIn(
            userData.userId,
            userData.userName,
            userData.subscriptionPlanName,
            userData.email,
            userData.avatarDisk,
            userData.imgUrl,
            userData.initials,
            userData.firstName
          )
        );
      }
      setTimeout(() => window.location.reload(), 100); // delayed to make sure local storage gets updated.
    });
  }, [dispatch]);

  return (
    <>
      {loggedIn ? (
        <AccountPreview />
      ) : (
        <Button
          variant="unstyled-button"
          onClick={onRequestLoginHandler}
          data-qa-id="login"
          className={styles.loginButton}
        >
          <Typography variant="paragraph3" semibold>
            Sign in
          </Typography>
        </Button>
      )}
      {createPortal(<LoginModalWrapper />, document.body)}
    </>
  );
};
